/**
 * webauthn-errors/session-lifecycle-messages.ts — session-expiry refusals
 *
 * Split out of messages.ts for the 300-line modularity rule, mirroring the
 * master-messages.ts / keypair-binding-messages.ts / challenge-race-messages.ts
 * / org-admin-messages.ts precedent, and re-exported into SERVER_ERROR_MESSAGES
 * so `resolveWebAuthnError` needs no separate lookup.
 *
 * Split at the 2026-09-02 merge of `origin/main` into the security-tier-ladder
 * stage-2 branch: the file sat at 298 lines on the merge base, this branch's
 * `MAX_SESSION_MS` copy correction added 3 and the org payment-defaults lane
 * (#2999, PR #3006) added 2, so the MERGE PRODUCT — not either side alone —
 * crossed the cap at 303. Nothing was deleted to get under it: these two codes
 * were lifted verbatim into their own table, which is the move the cap exists
 * to force.
 *
 * The two codes are the two session clocks, and the copy states each one's
 * duration in words. That is the reason they belong together and the reason
 * they are a drift hazard: both numbers are owned by `shared/session-policy.ts`
 * (`INACTIVITY_TIMEOUT_MS`, `MAX_SESSION_MS`) and are re-stated here as prose
 * a person reads. They are NOT interpolated from the constants, because these
 * strings are also the translation source and an interpolated duration is not
 * translatable as one phrase — so a retune of either constant MUST edit this
 * file in the same change. A person signed out after half an hour and told
 * about a "12-hour limit" is being given the wrong reason they were signed
 * out, which is exactly the drift this branch corrected (ledger #130).
 */

import type { WebAuthnUserMessage } from "./types";

export const SESSION_LIFECYCLE_ERROR_MESSAGES: Record<string, WebAuthnUserMessage> = {
  // 10 minutes — `INACTIVITY_TIMEOUT_MS`, unchanged by the 2026-08-31 directive.
  SESSION_EXPIRED_INACTIVE: {
    title:       "Session timed out",
    description: "Your session expired due to inactivity (10 minutes). Please sign in again.",
    retryable:   true,
    severity:    "amber",
  },

  // 30 min, not 12 h (owner directive 2026-08-31, superseding 2026-08-24 —
  // `MAX_SESSION_MS`). This is SHIPPED COPY: a person reading "12-hour limit"
  // after half an hour is being told the wrong reason they were signed out.
  SESSION_EXPIRED_ABSOLUTE: {
    title:       "Session reached maximum duration",
    description: "Your session has reached its 30-minute limit. Please sign in again.",
    retryable:   true,
    severity:    "amber",
  },
};
